class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    if(this.querySelector('[data-predictive-search]')){
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.predictiveSearchStatus = this.querySelector('.predictive-search-status');
    }
    else{
      this.predictiveSearchResults = this.closest('.search-parent-js').querySelector('[data-predictive-search]');
      this.predictiveSearchStatus = this.closest('.search-parent-js').querySelector('.predictive-search-status');
    }

    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    document.querySelector('.predictive-search__loading-state.hide') && document.querySelector('.predictive-search__loading-state.hide').classList.remove('hide');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange(e) {
    this.searchTerm = this.getQuery();

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if(this.classList.contains('static') && this.querySelector('.field__input').value != "" ) return false;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Shopify can return no predictive results for some buyer locales.
    // When the storefront is Arabic, query the English locale index only for
    // predictive search while keeping the actual storefront locale unchanged.
    const isArabicLocale = String(routes.current_locale || document.documentElement.lang || '').toLowerCase().startsWith('ar');
    const englishPredictiveUrl = routes.predictive_search_url_en || '';
    const primaryPredictiveUrl = isArabicLocale && englishPredictiveUrl
      ? englishPredictiveUrl
      : routes.predictive_search_url;

    const requestResults = (baseUrl) => fetch(`${baseUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal
    });

    requestResults(primaryPredictiveUrl)
      .then((response) => {
        // If the English route isn't available for a particular Market, fall back
        // to Shopify's normal locale-aware route rather than breaking search.
        if (!response.ok && primaryPredictiveUrl !== routes.predictive_search_url) {
          return requestResults(routes.predictive_search_url);
        }
        return response;
      })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
        const parsedSection = parsedDocument.querySelector('#shopify-section-predictive-search');
        if (!parsedSection) throw new Error('Predictive search section not found');

        // V4: Arabic storefronts may use the English predictive-search index to
        // get Shopify results, but the returned HTML must still behave as Arabic.
        if (isArabicLocale && primaryPredictiveUrl === englishPredictiveUrl) {
          this.localizeEnglishPredictiveResults(parsedSection, searchTerm);
          return this.localizeProductTitlesFromCurrentLocale(parsedSection).then(() => parsedSection);
        }

        return parsedSection;
      })
      .then((parsedSection) => {
        const resultsMarkup = parsedSection.innerHTML;
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  localizeEnglishPredictiveResults(section, searchTerm) {
    const labels = routes.predictive_search_labels || {};

    const setHeading = (selector, value) => {
      if (!value) return;
      const element = section.querySelector(selector);
      if (!element) return;
      const textNode = Array.from(element.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
      if (textNode) textNode.nodeValue = value;
      else element.insertAdjacentText('afterbegin', value);
    };

    setHeading('#predictive-search-queries', labels.suggestions);
    setHeading('#predictive-search-products', labels.products);
    setHeading('#predictive-search-collections', labels.collections);
    setHeading('#predictive-search-pages-mobile', labels.pages);

    const searchFor = section.querySelector('#search-for-button span');
    if (searchFor && labels.search_for) {
      searchFor.textContent = labels.search_for.replace('__WOKIEE_TERMS__', searchTerm);
    }

    // The English endpoint returns English-localized links. Rebase every
    // storefront-relative result URL to the CURRENT locale so selecting a
    // suggestion/product never switches the customer to English.
    const currentRoot = this.normalizeLocaleRoot(routes.root_url || '/');
    const englishRoot = this.normalizeLocaleRoot(routes.english_root_url || '/');

    section.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch (_) {
        return;
      }
      if (url.origin !== window.location.origin) return;

      let path = url.pathname || '/';
      if (englishRoot !== '/' && (path === englishRoot || path.startsWith(`${englishRoot}/`))) {
        path = path.slice(englishRoot.length) || '/';
      }

      // Avoid adding the current locale twice if Shopify already returned it.
      if (currentRoot !== '/' && (path === currentRoot || path.startsWith(`${currentRoot}/`))) {
        return;
      }

      const localizedPath = currentRoot === '/'
        ? path
        : `${currentRoot}${path.startsWith('/') ? path : `/${path}`}`;

      link.setAttribute('href', `${localizedPath}${url.search}${url.hash}`);
    });
  }


  async localizeProductTitlesFromCurrentLocale(section) {
    const productLinks = Array.from(section.querySelectorAll('a.predictive-search__item__product[href]'));
    if (!productLinks.length) return;

    const localeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root)
      ? Shopify.routes.root
      : ((routes.root_url || '/').replace(/\/?$/, '/'));

    await Promise.all(productLinks.map(async (link) => {
      let url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (_) {
        return;
      }

      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      if (!match || !match[1]) return;
      const handle = decodeURIComponent(match[1]);

      try {
        const response = await fetch(`${localeRoot}products/${encodeURIComponent(handle)}.js`, {
          headers: { 'Accept': 'application/json' },
          signal: this.abortController.signal
        });
        if (!response.ok) return;
        const localizedProduct = await response.json();
        if (!localizedProduct || !localizedProduct.title) return;

        const title = link.querySelector('.predictive-search__item-heading');
        if (title) title.textContent = localizedProduct.title;

        const image = link.querySelector('.predictive-search__image');
        if (image && localizedProduct.title && !image.getAttribute('alt')) {
          image.setAttribute('alt', localizedProduct.title);
        }
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // Keep the English predictive-search title as a safe fallback.
      }
    }));
  }

  normalizeLocaleRoot(root) {
    if (!root || root === '/') return '/';
    const normalized = `/${String(root).replace(/^\/+|\/+$/g, '')}`;
    return normalized || '/';
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.predictiveSearchStatus;
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    var count_value = false;
    if(this.querySelector('[data-predictive-search-live-region-count-value]')){
      count_value = this.querySelector('[data-predictive-search-live-region-count-value]');
    }
    else{
      count_value = this.closest('.search-parent-js').querySelector('[data-predictive-search-live-region-count-value]');
    }
    this.setLiveRegionText(count_value.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - this.getBoundingClientRect().bottom - 50;
    return this.resultsMaxHeight;
  }

  open() {
    if(!this.closest('.search-parent-js')){
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
